import { Link as RouterLink } from "react-router-dom";
import { Link, Chip, Box, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { fLocalTime, yearFirstDateTimeFormat, yearFirstDateTimeFormatUTC, yearFirstDateFormatUTC } from "src/utils/formatTime";
import { TagActionMenu } from "./TagActionMenu";
import { TagChargerActionMenu } from "./TagChargerActionMenu";
import Tooltip from "@mui/material/Tooltip";

export const tagListColumns = (isReadOnly) => [
  {
    field: "Actions",
    width: 50,
    sortable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <TagActionMenu id={id} row={row} />;
    },
  },
  {
    field: "tag",
    headerName: "RFID Tag",
    flex: 1.2,
    minWidth: 150,
    renderCell: ({ value, id }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{
          p: 0.5,
          borderRadius: 1,
          background: (theme) => theme.palette.primary.lighter,
          mr: 1,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Icon icon="eva:credit-card-outline" width={16} height={16} style={{ 
            color: (theme) => theme.palette.primary.main 
          }} />
        </Box>
        {isReadOnly ? (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
        ) : (
          <Link 
            underline="none" 
            component={RouterLink} 
            to={`/tag/${id}`}
            sx={{ 
              fontWeight: 500,
              color: (theme) => theme.palette.primary.main,
              '&:hover': {
                color: (theme) => theme.palette.primary.dark,
                textDecoration: 'underline'
              }
            }}
          >
            {value}
          </Link>
        )}
      </Box>
    ),
  },
  {
    field: "tag_type",
    headerName: "Type",
    width: 140,
    renderCell: ({ value, row }) => {
      if (value === "PARENT") {
        const hasBalance =
          row?.balance_amount !== null &&
          row?.balance_amount !== undefined &&
          row?.balance_amount !== "" &&
          Number(row?.balance_amount) > 0;
        return (
          <Chip
            label={hasBalance ? "Dept. Wallet" : "Parent"}
            size="small"
            color={hasBalance ? "success" : "primary"}
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
        );
      }
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {value || "RFID"}
        </Typography>
      );
    },
  },
  {
    field: "parenttag",
    headerName: "Parent Group",
    flex: 1,
    minWidth: 120,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {value ? (
          <Chip
            label={value}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              borderColor: (theme) => theme.palette.divider,
              color: (theme) => theme.palette.text.primary,
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />
        ) : (
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontStyle: 'italic'
          }}>
            No Group
          </Typography>
        )}
      </Box>
    ),
  },
  {
    field: "expirydate",
    headerName: "Expiry Date",
    flex: 1,
    minWidth: 140,
    renderCell: ({ value }) => {
      // Handle null/undefined values
      if (!value) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          </Box>
        );
      }

      // Parse dates as UTC to avoid timezone conversion issues
      // Extract just the date part (YYYY-MM-DD) for comparison
      const expiryDateStr = value.split('T')[0]; // Get YYYY-MM-DD part
      const currentDateStr = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD
      const thirtyDaysFromNowStr = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const isExpired = expiryDateStr < currentDateStr;
      const isExpiringSoon = expiryDateStr < thirtyDaysFromNowStr;
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1,
            background: isExpired 
              ? (theme) => theme.palette.error.lighter
              : isExpiringSoon 
                ? (theme) => theme.palette.warning.lighter
                : (theme) => theme.palette.success.lighter,
            mr: 1,
            display: 'flex',
            alignItems: 'center'
          }}>
            <Icon 
              icon={isExpired ? "eva:close-outline" : isExpiringSoon ? "eva:clock-outline" : "eva:checkmark-outline"} 
              width={14} 
              height={14} 
              style={{ 
                color: isExpired 
                  ? (theme) => theme.palette.error.main
                  : isExpiringSoon 
                    ? (theme) => theme.palette.warning.main
                    : (theme) => theme.palette.success.main
              }} 
            />
          </Box>
          <Tooltip title={yearFirstDateTimeFormatUTC(value)} placement="top">
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                color: isExpired 
                  ? (theme) => theme.palette.error.main
                  : isExpiringSoon 
                    ? (theme) => theme.palette.warning.main
                    : (theme) => theme.palette.text.primary
              }}
            >
              {yearFirstDateFormatUTC(value)}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    field: "in_transaction",
    headerName: "Status",
    flex: 0.8,
    minWidth: 100,
    renderCell: ({ value, row }) => {
      const isBlocked = row.blocked === "1" || row.blocked === 1 || row.blocked === true;
      const inTransaction = value === "1" || value === 1 || value === true;
      
      // Check if tag is expired (handle null, undefined, or invalid dates)
      const isExpired = (() => {
        if (!row.expirydate) return false;
        const expiryDate = new Date(row.expirydate);
        return !isNaN(expiryDate.getTime()) && expiryDate < new Date();
      })();
      
      if (isExpired) {
        return (
          <Chip
            icon={<Icon icon="eva:clock-outline" width={16} height={16} />}
            label="Expired"
            size="small"
            color="default"
            variant="filled"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
        );
      } else if (isBlocked) {
        return (
          <Chip
            icon={<Icon icon="eva:close-circle-outline" width={16} height={16} />}
            label="Blocked"
            size="small"
            color="error"
            variant="filled"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
        );
      } else if (inTransaction) {
        return (
          <Chip
            icon={<Icon icon="eva:flash-outline" width={16} height={16} />}
            label="In Use"
            size="small"
            color="warning"
            variant="filled"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
        );
      } else {
        return (
          <Chip
            icon={<Icon icon="eva:checkmark-circle-outline" width={16} height={16} />}
            label="Active"
            size="small"
            color="success"
            variant="filled"
            sx={{ fontWeight: 500, fontSize: '0.75rem' }}
          />
        );
      }
    },
  },
];

export const tagChargersColumns = (refetch) => [
  {
    field: "charge_box_id",
    headerName: "Chargerbox ID",
    width: 150,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "last_heartbeat_timestamp",
    headerName: "Last Heartbeat",
    type: "date",
    width: 180,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "Actions",
    width: 50,
    sortable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <TagChargerActionMenu id={id} row={row} refetch={refetch} />;
    },
  },
];
export const tagFormChargersColumns = () => [
  {
    field: "charge_box_id",
    headerName: "Chargerbox ID",
    flex: 1,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
  },
  {
    field: "last_heartbeat_timestamp",
    headerName: "Last Heartbeat",
    type: "date",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  // {
  //   field: "Actions",
  //   width: 70,
  //   sortable: false,
  //   renderCell: (props) => <TagChargerActionMenu {...props} refetch={refetch} />,
  // },
];

export const tagUsersColumn = [
  {
    field: "firstName",
    headerName: "First Name",
    width: 120,
  },
  {
    field: "lastName",
    headerName: "Last Name",
    width: 120,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
];

// Explicit creation modes — sent to the API as `parentKind`. Keeps the three
// creation flows (regular tag, reporting-only department parent, wallet-enabled
// department parent) unambiguous on both sides.
export const PARENT_KIND = {
  NONE: "NONE",
  REPORTING: "REPORTING",
  WALLET: "WALLET",
};

export const getTagDefaultValues = (teamId, userId) => ({
  tag: "",
  emailId: "",
  parenttag: "",
  expirydate: "",
  max_active_trans_count: 0,
  note: "",
  chargerpointId: 0,
  companyId: teamId,
  userId,
  balance_amount: "",
  blocked: false,
  in_transaction: false,
  parentKind: PARENT_KIND.NONE,
  isParent: false,
  tag_type: "",
  tagTransactionCount: 0,
  childCount: 0,
});
